import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Mail, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRACK_LABELS = {
  individual: "Individual (£800)",
  salon: "Salon / Clinic (£3,000)",
};

export default function Admin() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [enquiries, setEnquiries] = useState([]);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    if (location.state?.user) return; // user passed from AuthCallback — skip the check
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!response.ok) throw new Error("Not authenticated");
        const data = await response.json();
        setUser(data);
        setIsAuthenticated(true);
      } catch (e) {
        setIsAuthenticated(false);
        navigate("/admin/login", { replace: true });
      }
    };
    checkAuth();
  }, [location.state, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const load = async () => {
      try {
        const [enq, book] = await Promise.all([
          axios.get(`${API}/admin/enquiries`, { withCredentials: true }),
          axios.get(`${API}/admin/bookings`, { withCredentials: true }),
        ]);
        setEnquiries(enq.data);
        setBookings(book.data);
      } catch (e) {
        /* tables stay empty on error */
      }
    };
    load();
  }, [isAuthenticated]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      navigate("/admin/login", { replace: true });
    }
  };

  if (isAuthenticated === null) {
    return (
      <div className="bg-linen min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-8 h-8 text-forest animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="bg-linen text-ink font-sans min-h-screen">
      <header className="border-b border-cream px-6 md:px-12 py-5 flex items-center justify-between bg-linen/90 backdrop-blur sticky top-0 z-40">
        <div className="leading-tight">
          <span className="font-display text-lg font-semibold block">LymphoMetabolic Academy</span>
          <span className="font-monolbl text-[10px] uppercase tracking-widest text-forest/80">
            Owner area — {user?.email}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Link
            data-testid="admin-view-site-link"
            to="/"
            className="text-xs uppercase tracking-wider text-moss hover:text-ink transition-colors"
          >
            View site
          </Link>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="px-4 py-2 rounded-full border border-forest/30 text-forest text-xs font-semibold uppercase tracking-wider hover:border-forest transition-colors flex items-center gap-2"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-10 space-y-12">
        <section data-testid="admin-enquiries-section">
          <h2 className="font-display text-2xl mb-1 flex items-center gap-2">
            <Mail className="w-5 h-5 text-forest" /> Course enquiries
          </h2>
          <p className="text-xs text-moss mb-5">{enquiries.length} received</p>
          <div className="bg-white border border-cream rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-monolbl uppercase tracking-wider text-moss border-b border-cream">
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Phone</th>
                  <th className="px-5 py-3">Track</th>
                  <th className="px-5 py-3">Intake</th>
                  <th className="px-5 py-3">Notes</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map((e) => (
                  <tr key={e.id} data-testid={`enquiry-row-${e.id}`} className="border-b border-cream/60 last:border-0 align-top">
                    <td className="px-5 py-3 whitespace-nowrap text-moss">
                      {new Date(e.created_at).toLocaleDateString("en-GB")}
                    </td>
                    <td className="px-5 py-3 font-medium">{e.name}</td>
                    <td className="px-5 py-3">
                      <a href={`mailto:${e.email}`} className="text-forest underline">{e.email}</a>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">{e.phone || "—"}</td>
                    <td className="px-5 py-3 whitespace-nowrap">{TRACK_LABELS[e.applicant_type] || e.applicant_type}</td>
                    <td className="px-5 py-3 whitespace-nowrap">{e.cohort_label || "—"}</td>
                    <td className="px-5 py-3 text-moss max-w-xs">{e.message || "—"}</td>
                  </tr>
                ))}
                {enquiries.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-5 py-8 text-center text-moss">
                      No enquiries yet — they'll appear here as they arrive.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section data-testid="admin-bookings-section">
          <h2 className="font-display text-2xl mb-1 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-forest" /> Payments &amp; bookings
          </h2>
          <p className="text-xs text-moss mb-5">{bookings.length} checkout sessions</p>
          <div className="bg-white border border-cream rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-monolbl uppercase tracking-wider text-moss border-b border-cream">
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Track</th>
                  <th className="px-5 py-3">Amount</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Intake</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.session_id} data-testid={`booking-row-${b.session_id}`} className="border-b border-cream/60 last:border-0">
                    <td className="px-5 py-3 whitespace-nowrap text-moss">
                      {new Date(b.created_at).toLocaleDateString("en-GB")}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {b.lookup_key === "course_salon" ? "Salon / Clinic" : "Individual"}
                    </td>
                    <td className="px-5 py-3 font-medium">
                      £{b.amount.toLocaleString("en-GB")} {String(b.currency || "").toUpperCase()}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`px-2.5 py-1 rounded-full text-[11px] font-monolbl uppercase tracking-wider ${
                          b.payment_status === "paid"
                            ? "bg-forest/10 text-forest"
                            : "bg-sand text-moss"
                        }`}
                      >
                        {b.payment_status}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">{b.cohort || "—"}</td>
                  </tr>
                ))}
                {bookings.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-5 py-8 text-center text-moss">
                      No payments yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
